import { useMemo, useState, useCallback } from "react";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import DeleteOutlineIconButton from "components/common/DeleteOutlineIconButton";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Link as RouterLink } from "react-router-dom";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import useLocations from "hooks/locations/useLocations";
import useDeleteLocation from "hooks/locations/useDeleteLocation";
import { surfacePanelSx, portalDataGridSx } from "constants/pageSurfaces";
import { formatDate } from "utils/helpers";
import { Location } from "types/location";

function cell(v: string | undefined | null) {
  const s = v?.trim();
  return s ? s : "—";
}

function truncate(text: string | undefined | null, max: number) {
  const s = text?.trim();
  if (!s) return "—";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function createdDisplay(loc: Location) {
  const raw = loc.createdAt ?? loc.created_at;
  if (!raw) return "—";
  return formatDate(raw) ?? "—";
}

function locationSearchHaystack(loc: Location) {
  return [
    loc.name,
    loc.address,
    loc.address2,
    loc.city,
    loc.state,
    loc.zipcode,
    loc.country,
    loc.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export default function LocationsList() {
  const { locations, isLoading, refetch } = useLocations();
  const { deleteLocation } = useDeleteLocation(refetch);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredLocations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((loc) => locationSearchHaystack(loc).includes(q));
  }, [locations, searchQuery]);

  const onDelete = useCallback(
    (loc: Location) => {
      if (loc.uuid && window.confirm("Delete this location?")) {
        deleteLocation(loc);
      }
    },
    [deleteLocation]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => {
          const loc = params.row as Location;
          return <RouterLink to={`/locations/${loc.uuid}/edit`}>{cell(loc.name)}</RouterLink>;
        },
      },
      {
        field: "address",
        headerName: "Address",
        minWidth: 160,
        flex: 0.7,
        valueGetter: (p) => cell((p.row as Location).address),
      },
      {
        field: "address2",
        headerName: "Line 2",
        minWidth: 120,
        flex: 0.5,
        valueGetter: (p) => cell((p.row as Location).address2),
      },
      {
        field: "city",
        headerName: "City",
        width: 110,
        valueGetter: (p) => cell((p.row as Location).city),
      },
      {
        field: "state",
        headerName: "State",
        width: 80,
        valueGetter: (p) => cell((p.row as Location).state),
      },
      {
        field: "zipcode",
        headerName: "Postal code",
        width: 100,
        valueGetter: (p) => cell((p.row as Location).zipcode),
      },
      {
        field: "country",
        headerName: "Country",
        width: 100,
        valueGetter: (p) => cell((p.row as Location).country),
      },
      {
        field: "stock_count",
        headerName: "Stock",
        width: 88,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueGetter: (p) => {
          const loc = p.row as Location;
          const n = loc.stockCount ?? loc.stock_count;
          return typeof n === "number" ? n : 0;
        },
      },
      {
        field: "description",
        headerName: "Building details",
        minWidth: 200,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const loc = params.row as Location;
          const desc = loc.description?.trim();
          return (
            <span title={desc || undefined}>{truncate(desc, 80)}</span>
          );
        },
      },
      {
        field: "created",
        headerName: "Created",
        width: 120,
        sortable: false,
        valueGetter: (p) => createdDisplay(p.row as Location),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 72,
        align: "right",
        headerAlign: "right",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <DeleteOutlineIconButton
            aria-label="Delete location"
            onClick={() => onDelete(params.row as Location)}
          />
        ),
      },
    ],
    [onDelete]
  );

  const noRowsMessage =
    locations.length === 0 ? "No locations yet." : "No locations match your search.";

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Locations" },
          ]}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Locations
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <TextField
            size="small"
            placeholder="Search locations…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              minWidth: { xs: "100%", sm: 260 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" },
            }}
          />
          <Button component={RouterLink} variant="contained" size="large" to="/locations/new" sx={{ flexShrink: 0 }}>
            New location
          </Button>
        </Box>
      </Box>
      <Paper variant="outlined" sx={{ ...surfacePanelSx, width: "100%", overflow: "hidden" }}>
        <DataGridPro
          autoHeight
          disableColumnMenu
          loading={isLoading}
          rows={filteredLocations}
          columns={columns}
          getRowId={(row) => row.uuid || String(row.id)}
          rowHeight={52}
          sx={portalDataGridSx}
          initialState={{ pinnedColumns: { right: ["actions"] } }}
          hideFooter
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>{noRowsMessage}</Box>
            ),
          }}
        />
      </Paper>
    </div>
  );
}
